import { useState, useEffect } from 'react';
import { User, UserPlus } from 'lucide-react';
import { apiService } from '@/services/api';
import { Usuario, UsuarioExterno } from '@/types/usuario';
import { useI18n } from '@/contexts/i18n-hook';

interface SelecaoUsuarioProps {
  label: string;
  /** Rótulo do campo nome no formulário de dados externos (ex.: "Executante", "Verificador", "Responsável Técnico").
   *  Sem valor, cai no rótulo padrão traduzido. */
  rotuloNome?: string;
  empresaId?: string;
  usuarioId?: string;
  usuarioExterno?: UsuarioExterno | null;
  onChange?: (usuarioId: string | null, usuarioExterno: UsuarioExterno | null) => void;
  modoEdicao?: boolean;
  required?: boolean;
}

export function SelecaoUsuario({
  label,
  rotuloNome,
  empresaId,
  usuarioId,
  usuarioExterno,
  onChange,
  modoEdicao = false,
  required = false,
}: SelecaoUsuarioProps) {
  const { t } = useI18n();
  const rotuloNomeEfetivo = rotuloNome ?? t('apreciacaoModais.selecaoUsuario.rotuloNomePadrao');
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [usarExterno, setUsarExterno] = useState(!!usuarioExterno);
  const [dadosExternos, setDadosExternos] = useState<UsuarioExterno>({
    nome: usuarioExterno?.nome || '',
    crea: usuarioExterno?.crea || '',
    cargo: usuarioExterno?.cargo || '',
    funcao: usuarioExterno?.funcao || '',
    empresa: usuarioExterno?.empresa || '',
    assinaturaUrl: usuarioExterno?.assinaturaUrl || '',
    assinaturaDigital: usuarioExterno?.assinaturaDigital || '',
    outrasInformacoes: usuarioExterno?.outrasInformacoes || '',
  });

  useEffect(() => {
    if (empresaId && !usarExterno) {
      carregarUsuarios();
    } else if (!empresaId) {
      console.warn('SelecaoUsuario: empresaId não fornecido. Não será possível carregar usuários.');
    }
  }, [empresaId, usarExterno]);

  // Sincronizar dados externos quando o pai altera (ex.: botão "Usar Lauan")
  useEffect(() => {
    if (usuarioExterno && usarExterno) {
      setDadosExternos({
        nome: usuarioExterno.nome ?? '',
        crea: usuarioExterno.crea ?? '',
        cargo: usuarioExterno.cargo ?? '',
        funcao: usuarioExterno.funcao ?? '',
        empresa: usuarioExterno.empresa ?? '',
        assinaturaUrl: usuarioExterno.assinaturaUrl ?? '',
        assinaturaDigital: usuarioExterno.assinaturaDigital ?? '',
        outrasInformacoes: usuarioExterno.outrasInformacoes ?? '',
      });
    }
  }, [usuarioExterno, usarExterno]);

  // Carregar dados do usuário quando selecionado
  useEffect(() => {
    if (usuarioId && !usarExterno && usuarios.length > 0) {
      const usuarioSelecionado = usuarios.find(u => u.id === usuarioId);
      if (usuarioSelecionado) {
        // Quando um usuário do sistema é selecionado, os dados já vêm do backend
        // Não precisamos fazer nada aqui, pois o onChange já foi chamado
      }
    }
  }, [usuarioId, usuarios, usarExterno]);

  const carregarUsuarios = async () => {
    if (!empresaId) return;

    setCarregando(true);
    try {
      const response = await apiService.listarUsuariosPorEmpresa(empresaId, true);
      if (response.success && response.data) {
        setUsuarios(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setCarregando(false);
    }
  };

  const handleToggleModo = () => {
    const novoModo = !usarExterno;
    setUsarExterno(novoModo);
    if (novoModo) {
      onChange?.(null, dadosExternos);
    } else {
      onChange?.(usuarioId || null, null);
    }
  };

  const handleUsuarioChange = async (novoUsuarioId: string) => {
    if (!novoUsuarioId) {
      onChange?.(null, null);
      return;
    }

    // Buscar dados completos do usuário selecionado
    try {
      const response = await apiService.buscarUsuario(novoUsuarioId);
      if (response.success && response.data) {
        // Quando um usuário do sistema é selecionado, os dados vêm do backend
        // O onChange será chamado com o ID, e o componente pai deve buscar os dados
        onChange?.(novoUsuarioId, null);
      } else {
        onChange?.(novoUsuarioId, null);
      }
    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      // Mesmo com erro, permitir selecionar o usuário
      onChange?.(novoUsuarioId, null);
    }
  };

  const handleExternoChange = (campo: keyof UsuarioExterno, valor: string) => {
    const novosDados = { ...dadosExternos, [campo]: valor };
    setDadosExternos(novosDados);
    onChange?.(null, novosDados);
  };

  if (!modoEdicao) {
    if (usarExterno || usuarioExterno) {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            {label}
          </label>
          <div className="text-sm text-gray-900 dark:text-white">
            <p><strong>{rotuloNomeEfetivo}:</strong> {usuarioExterno?.nome || dadosExternos.nome || '-'}</p>
            {(usuarioExterno?.crea ?? dadosExternos.crea) && <p><strong>{t('apreciacaoModais.selecaoUsuario.crea')}:</strong> {usuarioExterno?.crea ?? dadosExternos.crea}</p>}
            {(usuarioExterno?.funcao ?? dadosExternos.funcao) && (
              <p className="whitespace-pre-wrap"><strong>{t('apreciacaoModais.selecaoUsuario.funcao')}:</strong><br />{usuarioExterno?.funcao ?? dadosExternos.funcao}</p>
            )}
            {(usuarioExterno?.empresa ?? dadosExternos.empresa) && <p><strong>{t('comum.rotulo.empresa')}:</strong> {usuarioExterno?.empresa ?? dadosExternos.empresa}</p>}
          </div>
        </div>
      );
    }

    const usuario = usuarios.find(u => u.id === usuarioId);
    if (usuario) {
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
            {label}
          </label>
          <div className="text-sm text-gray-900 dark:text-white">
            <p><strong>{t('comum.rotulo.nome')}:</strong> {usuario.nome}</p>
            {usuario.crea && <p><strong>{t('apreciacaoModais.selecaoUsuario.crea')}:</strong> {usuario.crea}</p>}
            {usuario.cargo && <p><strong>{t('apreciacaoModais.selecaoUsuario.cargo')}:</strong> {usuario.cargo}</p>}
            {usuario.empresa && <p><strong>{t('comum.rotulo.empresa')}:</strong> {usuario.empresa.nome}</p>}
          </div>
        </div>
      );
    }

    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
          {label}
        </label>
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('apreciacaoModais.selecaoUsuario.naoSelecionado')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-white">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <button
          type="button"
          onClick={handleToggleModo}
          className="flex items-center gap-2 text-sm text-blue-600 dark:text-schmersal-blue hover:underline"
        >
          {usarExterno ? (
            <>
              <User className="h-4 w-4" />
              {t('apreciacaoModais.selecaoUsuario.usarUsuarioSistema')}
            </>
          ) : (
            <>
              <UserPlus className="h-4 w-4" />
              {t('apreciacaoModais.selecaoUsuario.usarDadosExternos')}
            </>
          )}
        </button>
      </div>

      {usarExterno ? (
        <div className="space-y-3 p-4 border border-gray-300 dark:border-schmersal-gray-dark rounded-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
              {rotuloNomeEfetivo} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={dadosExternos.nome}
              onChange={(e) => handleExternoChange('nome', e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              required={required}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
              {t('apreciacaoModais.selecaoUsuario.funcao')}
            </label>
            <textarea
              value={dadosExternos.funcao ?? ''}
              onChange={(e) => handleExternoChange('funcao', e.target.value)}
              rows={3}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm resize-y"
              placeholder={t('apreciacaoModais.selecaoUsuario.funcaoPlaceholder')}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
                {t('apreciacaoModais.selecaoUsuario.crea')}
              </label>
              <input
                type="text"
                value={dadosExternos.crea}
                onChange={(e) => handleExternoChange('crea', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
                {t('comum.rotulo.empresa')}
              </label>
              <input
                type="text"
                value={dadosExternos.empresa}
                onChange={(e) => handleExternoChange('empresa', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                         focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                         bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                         sm:text-sm"
              />
            </div>
          </div>
        </div>
      ) : (
        <div>
          {carregando ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('apreciacaoModais.selecaoUsuario.carregandoUsuarios')}</p>
          ) : (
            <select
              value={usuarioId || ''}
              onChange={(e) => handleUsuarioChange(e.target.value)}
              className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm 
                       focus:ring-blue-500 dark:focus:ring-schmersal-blue focus:border-blue-500 dark:focus:border-schmersal-blue 
                       bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white
                       sm:text-sm"
              required={required}
            >
              <option value="">{t('apreciacaoModais.selecaoUsuario.selecioneUsuario')}</option>
              {usuarios.map((usuario) => (
                <option key={usuario.id} value={usuario.id}>
                  {usuario.nome} {usuario.crea && `(${usuario.crea})`} {usuario.cargo && `- ${usuario.cargo}`}
                </option>
              ))}
            </select>
          )}
          {!empresaId && (
            <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
              {t('apreciacaoModais.selecaoUsuario.semEmpresa')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

